// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { createContext, useContext, useEffect, useState } from "react"
import { IonContent } from "@ionic/react"
import useSession from "./useSession"
import ErrorPageContent from "./ErrorPageContent"
import { FatalErrorContainer } from "./FatalErrorContainer"
import FeatureNotAvailable from "./FeatureNotAvailable"
import SectionPageHeader from "./SectionPageHeader"
import SectionContent from "./SectionContent"
import SectionLoading from "./SectionLoading"
import LoginRequiredContainer from "./LoginRequiredContainer"
import './SectionContainer.scss'
import { useSectionDimensions } from "./useSectionDimensions"
import { KnowledgeBasePanel } from "./KnowledgeBasePanel"


interface SectionContainerProps {
  anonymous?: boolean
  booting?: boolean
  canGoBack?: boolean
  children: React.ReactNode
  contextPanel?: React.FC<any> | null
  contextPanelProps?: any
  loading?: boolean
  loginComponent?: React.FC<{}>
  menuComponent?: React.FC<any>
  nopadding?: boolean
  ready?: boolean
  section?: string
  title?: string
  toolbarComponent?: React.FC<any>
  toolbarProps?: any
}


interface IActiveContextMenu {
  component?: React.FC<any> | null
  componentProps?: any | null
}


interface IContextMenuContext extends IActiveContextMenu {
  contextPanelComponent: React.FC<any> | null
  helpArticle: string | null
  close: () => void
  isOpen: () => boolean
  render: () => React.ReactElement | null
  setContextMenu: (component: React.FC<any>, props?: any) => void
  setHelpArticle: (path: string | null) => void 
}


const ContextMenuContext: React.Context<IContextMenuContext> = createContext<IContextMenuContext>({
  contextPanelComponent: null,
  helpArticle: null,
  close: () => {},
  isOpen: () => { return false },
  render: () => { return null},
  setContextMenu: (component: React.FC<any>, props?: any): void => {},
  setHelpArticle: (path: string | null): void => {}
})

const useContextMenu: () => IContextMenuContext = () => {
  return useContext(ContextMenuContext)
}


const SectionContainer: React.FC<SectionContainerProps> = ({
  anonymous,
  booting,
  canGoBack,
  children,
  contextPanel,
  contextPanelProps,
  nopadding,
  loading,
  ready,
  section,
  toolbarComponent,
  toolbarProps,
  ...props
}) => {
  const params = new URLSearchParams(window.location.search)
  const {containerStyle, contentClasses} = useSectionDimensions()
  const SectionToolbar = toolbarComponent
  const {error, user, ...session} = useSession()
  const mustLogin = !anonymous && !user.isAuthenticated()
  const [contextMenu, setContextMenu] = useState<IActiveContextMenu>({})
  const LoginComponent: React.FC<any> = props.loginComponent || LoginRequiredContainer
  const ctx: IContextMenuContext = {
    contextPanelComponent: contextMenu.component || null,
    close: () => {
      setContextMenu({})
    },
    helpArticle: session.articlePath,
    render: () => {
      if (!contextMenu.component) { return null }
      return (<contextMenu.component {...(contextMenu.componentProps || {})}/>)
    },
    isOpen: () => !!contextMenu.component,
    setContextMenu: (component: React.FC<any>, props?: any): void => {
      setContextMenu({
        component: component,
        componentProps: props
      })
    },
    setHelpArticle: session.setArticlePath
  }
  let content: React.ReactNode | null = (!mustLogin)
    ? (children || (<FeatureNotAvailable/>))
    : (<LoginComponent/>)
  if (!!error) {
    content = (<ErrorPageContent error={error}/>)
  }
  else if (!!params.get("error")) {
    content = (<FatalErrorContainer error={params.get("error") as string}/>)
  }

  // Finally, set loading spinner if the application is booting.
  if (!!booting || session.booting) {
    content = (<SectionLoading/>)
  }

  useEffect(() => {
    if (!!error) {
      session.setLoading(false)
    }
  }, [error])

  return (
    <IonContent fullscreen scrollY={false}>
      <ContextMenuContext.Provider value={ctx}>
        <div className="section-container" style={containerStyle as React.CSSProperties}>
          {(!!props.menuComponent) ? (<props.menuComponent/>) : null}
          <div className={contentClasses.join(" ")}>
            <SectionPageHeader canGoBack={canGoBack} section={section} title={props.title}>
              {(!!SectionToolbar) ? (<SectionToolbar {...(toolbarProps || {})}/>) : null}
            </SectionPageHeader>
            <SectionContent
              contextPanel={contextPanel}
              contextPanelProps={contextPanelProps}
              nogutter={nopadding}
            >
              {(!!ready || !!error) ? content : null}
            </SectionContent>
          </div>
          <KnowledgeBasePanel
            article={session.articlePath}
            articleContent={session.articleContent}
            isOpen={!!session.articleContent}
            setArticle={session.setArticlePath}
            setContent={session.setArticleContent}
          />
        </div>
      </ContextMenuContext.Provider>
    </IonContent>
  )
}


export { useContextMenu, SectionContainer }
export type { SectionContainerProps }